// src/oura/docs/fillState.ts
//
// Whether a just-created doc has its content yet (#4051 item 1).
//
// A template copy and a skill run are both FILLED AFTER THE DOC EXISTS: Create
// answers as soon as the copy is in Drive, and the fill takes 45 to 120
// seconds. Opened in that window the doc is the raw template -- every
// [bracket], every "Guide:" box -- which reads as a failed fill. The create
// drawer was saying "Created", "Read it before you share", and offering Google
// Docs as its primary action the whole time.
//
// THE SERVER SENDS NO FILL STATE FOR A TEMPLATE CREATE. `CreateDocResult.run`
// is null for every kind but "skill", and nothing on the document says it is
// being filled. So this reads the one signal there is -- a skill run's status,
// else the kind -- and never guesses from a timer. When a fill state does
// arrive, this is the one place that decides.

import type { CreateDocKind, SkillRun } from "@service/aeo/clientDocsWrite";

export type DocFillState =
    /** Being filled in. Nothing to read, and not to be offered as ready. */
    | "filling"
    /** The fill ended without filling it. The doc exists and reads as the template. */
    | "failed"
    /** There is nothing to wait for: a linked or converted doc, or a finished run. */
    | "ready";

export function docFillState(kind: CreateDocKind, run: Pick<SkillRun, "status"> | null): DocFillState {
    // A run's own status beats the kind: it is the server speaking.
    if (run) {
        if (run.status === "failed" || run.status === "stopped_at_cap") return "failed";
        return run.status === "running" ? "filling" : "ready";
    }
    // "link" files a doc that already has its content; "convert" converts one.
    // A template copy is empty until the server fills it.
    return kind === "template" ? "filling" : "ready";
}
